import {
  Avatar,
  Box,
  CircularProgress,
  Typography,
  useTheme
} from "@mui/material";
import React from "react";
import { tokens } from "../../../theme";
import { AssignableUser } from "./venues-widget.types";

interface UserAvatarRowProps {
  users: AssignableUser[];
  loading: boolean;
  onDragStart: (user: AssignableUser) => void;
}

const UserAvatarRow: React.FC<UserAvatarRowProps> = ({
  users,
  loading,
  onDragStart,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color={colors.primary[400]}
        sx={{ display: "block", mb: 1, letterSpacing: "0.05em" }}
      >
        DRAG USER TO ASSIGN SITE VISIT
      </Typography>

      {loading ? (
        <CircularProgress size={20} sx={{ color: colors.primary[400] }} />
      ) : users.length === 0 ? (
        <Typography variant="caption" color={colors.primary[300]}>
          No assignable users found
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            pb: 0.5,
          }}
        >
          {users.map((user) => {
            const initials =
              `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() ||
              "?";

            return (
              <Box
                key={user.id}
                draggable
                onDragStart={() => onDragStart(user)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "grab",
                  userSelect: "none",
                  "&:active": { cursor: "grabbing" },
                }}
              >
                <Avatar
                  src={user.profile_picture_url ?? undefined}
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: colors.primary[400],
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    border: `2px solid ${colors.primary[700]}`,
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                    "&:hover": {
                      boxShadow: `0 4px 12px rgba(0,0,0,0.2)`,
                    },
                  }}
                >
                  {initials}
                </Avatar>

                <Typography
                  variant="caption"
                  fontSize="0.65rem"
                  color={colors.primary[300]}
                  noWrap
                  sx={{ maxWidth: 60, textAlign: "center" }}
                >
                  {user.first_name || "User"}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default UserAvatarRow;
